import { IRenewalInvoiceStatus } from "../../services/api/subscription/types";
import {
  CREATE_RENEWAL_INVOICE,
  CREATE_RENEWAL_INVOICE_ERROR,
  CREATE_RENEWAL_INVOICE_SUCCESS,
  FETCH_RENEWAL_INVOICE_STATUS,
  FETCH_RENEWAL_INVOICE_STATUS_ERROR,
  FETCH_RENEWAL_INVOICE_STATUS_SUCCESS,
  initialState,
} from "./constants";
import { ActionTypes, IRenewalInvoiceState } from "./types";

// Hash of server status fields; used to detect stale refusals across status changes.
const statusSignature = (
  status?: IRenewalInvoiceStatus,
): string | undefined => {
  if (!status) {
    return undefined;
  }

  return [
    status.payable,
    status.payable_from,
    status.payable_until,
    status.invoice?.sp_invoice_id,
    status.invoice?.is_open,
  ].join("|");
};

const reducer = (
  prevState: IRenewalInvoiceState,
  action: ActionTypes,
): IRenewalInvoiceState => {
  const state = prevState || initialState;

  switch (action.type) {
    case FETCH_RENEWAL_INVOICE_STATUS:
      return {
        ...state,
        isFetchingById: {
          ...state.isFetchingById,
          [action.payload.subscriptionId]: true,
        },
      };
    case FETCH_RENEWAL_INVOICE_STATUS_SUCCESS: {
      const { subscriptionId, status } = action.payload;
      // Clear stale refusal when server state changes.
      const refusedAgainst = state.errorStatusSignatureById[subscriptionId];
      const isErrorStale =
        state.errorById[subscriptionId] !== undefined &&
        refusedAgainst !== undefined &&
        refusedAgainst !== statusSignature(status);

      return {
        ...state,
        statusById: {
          ...state.statusById,
          [subscriptionId]: status,
        },
        isFetchingById: {
          ...state.isFetchingById,
          [subscriptionId]: false,
        },
        statusFailedById: {
          ...state.statusFailedById,
          [subscriptionId]: false,
        },
        statusErrorById: {
          ...state.statusErrorById,
          [subscriptionId]: undefined,
        },
        errorById: isErrorStale
          ? { ...state.errorById, [subscriptionId]: undefined }
          : state.errorById,
        errorStatusSignatureById: isErrorStale
          ? { ...state.errorStatusSignatureById, [subscriptionId]: undefined }
          : state.errorStatusSignatureById,
        // Clear dead checkout when server reports no live invoice.
        lastCreated:
          state.lastCreated?.subscriptionId === subscriptionId &&
          !status?.invoice
            ? undefined
            : state.lastCreated,
      };
    }
    case FETCH_RENEWAL_INVOICE_STATUS_ERROR:
      return {
        ...state,
        isFetchingById: {
          ...state.isFetchingById,
          [action.payload.subscriptionId]: false,
        },
        statusFailedById: {
          ...state.statusFailedById,
          [action.payload.subscriptionId]: true,
        },
        statusErrorById: {
          ...state.statusErrorById,
          [action.payload.subscriptionId]: action.payload.message,
        },
      };
    case CREATE_RENEWAL_INVOICE:
      return {
        ...state,
        isCreatingById: {
          ...state.isCreatingById,
          [action.payload.subscriptionId]: true,
        },
        errorById: {
          ...state.errorById,
          [action.payload.subscriptionId]: undefined,
        },
        errorStatusSignatureById: {
          ...state.errorStatusSignatureById,
          [action.payload.subscriptionId]: undefined,
        },
      };
    case CREATE_RENEWAL_INVOICE_SUCCESS:
      return {
        ...state,
        isCreatingById: {
          ...state.isCreatingById,
          [action.payload.subscriptionId]: false,
        },
        errorById: {
          ...state.errorById,
          [action.payload.subscriptionId]: undefined,
        },
        errorStatusSignatureById: {
          ...state.errorStatusSignatureById,
          [action.payload.subscriptionId]: undefined,
        },
        lastCreated: action.payload,
      };
    case CREATE_RENEWAL_INVOICE_ERROR:
      return {
        ...state,
        isCreatingById: {
          ...state.isCreatingById,
          [action.payload.subscriptionId]: false,
        },
        errorById: {
          ...state.errorById,
          [action.payload.subscriptionId]: action.payload.message,
        },
        errorStatusSignatureById: {
          ...state.errorStatusSignatureById,
          [action.payload.subscriptionId]: statusSignature(
            state.statusById[action.payload.subscriptionId],
          ),
        },
      };
    default:
      return state;
  }
};

export default reducer;
